/**
 * mergeConfig — merge overrides, fetched values, and convention defaults.
 *
 * Priority (per field, first match wins):
 *   1. env var overrides
 *   2. fetched remote values
 *   3. convention-derived safe defaults
 *
 * Throws ConfigurationError when required fields are missing.
 */

import type {
  ResolveConfigOptions,
  RemoteConfiguration,
  ConventionDefaults,
  ClientConfiguration,
} from './types';
import { ConfigurationError } from './types';

const DEFAULT_STAGE = 'local';
const DEFAULT_TRACES_SAMPLE_RATE = 0.1;
const DEFAULT_TELEGRAM_BOT_NAME = 'deaddrop_service_bot';

/**
 * Merge the three config tiers into a validated ClientConfiguration.
 *
 * Throws ConfigurationError if auth.publishableKey or service.apiUrl
 * cannot be resolved.
 */
export function mergeConfig(
  overrides: ResolveConfigOptions['overrides'],
  fetched: RemoteConfiguration | null,
  conventions: ConventionDefaults
): ClientConfiguration {
  const safeOverrides = overrides ?? {};

  // ─── service.apiUrl ───────────────────────────────────────────────────────
  const apiUrl = safeOverrides.apiUrl || fetched?.service.api_url || conventions.apiUrl || null;

  if (!apiUrl) {
    throw new ConfigurationError(
      'Configuration is incomplete: service.apiUrl is required but could not be resolved.'
    );
  }

  // ─── service.originsWeb ──────────────────────────────────────────────────
  const originsWeb = fetched?.service.origins_web ?? '';

  // ─── service.version ─────────────────────────────────────────────────────
  const version = fetched?.service.version;

  // ─── service.stage ───────────────────────────────────────────────────────
  const stage = fetched?.service.stage ?? DEFAULT_STAGE;

  // ─── auth.publishableKey ─────────────────────────────────────────────────
  const publishableKey = safeOverrides.clerkPublishableKey || fetched?.auth.publishable_key || null;

  if (!publishableKey) {
    throw new ConfigurationError(
      'Configuration is incomplete: auth.publishableKey is required but could not be resolved.'
    );
  }

  // ─── sentry ─────────────────────────────────────────────────────────────
  const sentry = {
    dsn: fetched?.sentry?.dsn ?? null,
    tracesSampleRate: fetched?.sentry?.traces_sample_rate ?? DEFAULT_TRACES_SAMPLE_RATE,
    environment: fetched?.sentry?.environment ?? stage,
  };

  // ─── features ────────────────────────────────────────────────────────────
  const presignedUploads = fetched?.features.presigned_uploads ?? false;
  const telegramBotName =
    safeOverrides.telegramBotName ||
    fetched?.features.telegram_bot_name ||
    DEFAULT_TELEGRAM_BOT_NAME;
  const waitlistUrl = safeOverrides.waitlistUrl || fetched?.features.waitlist_url || null;

  const config: ClientConfiguration = {
    service: {
      version,
      apiUrl,
      originsWeb,
      stage,
    },
    auth: {
      publishableKey,
    },
    sentry,
    features: {
      presignedUploads,
      telegramBotName,
      waitlistUrl,
    },
  };

  return config;
}
